import React, {FC} from "react";
import {
   GlobalLayout,
   PanelLayout,
   StyledButton,
   StyledDiv,
   StyledTypography
} from "../../providers/layout/LayoutProvider";
import {makeStyles} from "@mui/styles";
import {usePublicPageConsumerCreateMutation} from "../../Queries";
import clsx from "clsx";
import {Button, darken, styled, Theme} from "@mui/material";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {AccessContextData} from "../../providers/access/AppContextProvider";
import {LocalizationsAtom} from "../../providers/localization/LocalizationProvider";
import {ReturnsPagePath} from "../restricted/returns/ReturnsPage";
import {useNavigate} from "react-router-dom";
import {CountryMapFactory} from "../../utility/CountryMapFactory";
import {DropOffIcon, PackItIcon, TradeInIcon} from "../../svg/SvgIcons";
import {LoginContext} from "../LoginContext";

export const OutfitPage: FC<any> = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useStyles({
      leftImage: localizations.frontPageImage1 || "url('https://cdn.sanity.io/images/frvne2mj/production/d8118e6eae704a778c6227dd2fa97237059a7338-1795x977.jpg?dl=md&w=2200&auto=format&fit=max')",
      rightImage: localizations.frontPageImage2 || "url('https://cdn.sanity.io/images/frvne2mj/production/a933060588a4fc169391706472a825543b28aeba-3600x2400.jpg?dl=md&w=2200&auto=format&fit=max')",
   });

   return <div>
      <StyledDiv type={"main"} className={classes.container}>
         <StyledDiv type={"main"} className={clsx(classes.image, classes.leftImage)}>
            &nbsp;
         </StyledDiv>
         <StyledDiv type={"contrast"} className={classes.main}>
            <div className={classes.content}>
               <StyledTypography variant={"h1"} type={"contrast"} style={{marginBottom: '26px'}}>
                  {localizations.loginPageTitle}
               </StyledTypography>
               <StyledTypography variant={"body1"} type={"contrast"} style={{marginBottom: '20px'}}>
                  {localizations.loginPageDescription}<br/>
                  {localizations.loginPageDescription2}
               </StyledTypography>
               <div className={classes.accessForm}>
                  <OutfitAccessForm/>
               </div>
            </div>
         </StyledDiv>
         <StyledDiv type={"main"} className={clsx(classes.image, classes.rightImage)}>
            &nbsp;
         </StyledDiv>
      </StyledDiv>
      <OutfitEasyBox/>
   </div>
}

type imageProps = {
   leftImage: string
   rightImage: string
}

const useStyles = makeStyles({
   backContainer: {
      marginTop: '20px'
   },
   content: {
      maxWidth: '500px',
      textAlign: 'center'
   },
   container: {
      display: 'flex',
      width: '100%',
      flexDirection: 'row',
      alignItems: 'top',
      padding: '0 40px 0 40px'
   },
   accessForm: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px'
   },
   image: {
      width: '400px',
      minHeight: '496px',
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "top center",
      overflowX: 'hidden',
      overflowY: 'hidden',
   },
   main: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      flexGrow: 1,
      padding: '50px'
   },
   leftImage: {
      backgroundImage: (props: imageProps) => props.leftImage
   },
   rightImage: {
      backgroundImage: (props: imageProps) => props.rightImage
   },
   '@media only screen and (max-width: 800px)': {
      container: {
         flexDirection: 'column',
         padding: '0 23px 0 23px'
      } as any,
      image: {
         width: '100%',
         maxHeight: '200px',
         minHeight: '300px',
         backgroundPosition: "center",
      }
   },
   '@media only screen and (max-width: 1024px)': {
      container: {
         padding: '0 23px 0 23px'
      } as any
   },
})

const OutfitAccessForm = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const appContext = useRecoilValue(AccessContextData);
   const [createConsumer] = usePublicPageConsumerCreateMutation()
   const navigate = useNavigate();
   const setLoginContext = useSetRecoilState(LoginContext)

   const onCreate = async () => {
      const userLang = navigator.language || (navigator as any).userLanguage || 'da';
      const countries = CountryMapFactory.getCountriesFromDomain(window.location.hostname)
      const country = countries?.[0]?.alpha2 || undefined

      const {data} = await createConsumer({
         variables: {
            shopId: appContext.shopId,
            locale: userLang,
            country: window.Shopify?.country || window.tradeInSession?.countryCode || country,
            currency: appContext.currency || window.Shopify?.currency?.active || window.tradeInSession?.currency
         }
      });
      if (data?.ConsumerCreate?.accessToken) {
         setLoginContext({
            accessToken: data?.ConsumerCreate?.accessToken
         })
         navigate(ReturnsPagePath());
      }
   }

   return <>
      <StyledButton
         styletype={"contrast"}
         variant="contained"
         id="email"
         onClick={onCreate}
         sx={{
            cursor: 'pointer',
            marginTop: '20px'
         }}
      >
         {localizations.publicPageWholeSaleTitle}
      </StyledButton>
   </>
}


type StyledButtonProps = {
   styletype: 'main' | 'contrast'
}

export const OutfitButton = styled(Button)<StyledButtonProps>(({theme, variant, styletype}) => {
   const panelLayout = useRecoilValue(PanelLayout(styletype));
   const globalLayout = useRecoilValue(GlobalLayout);

   const button = panelLayout?.mainButton;

   return {
      color: '#56352C' || button?.textColor || undefined,
      '&:hover': {
         color: '#56352C' || button?.textColor || undefined,
         border: button?.border || undefined,
         backgroundColor: 'transparent'
      },
      '&:disabled': {
         color: button?.disabledTextColor || darken(button?.textColor || '#000000', 0.5) + ' !important',
      },
      fontSize: button?.fontSize || globalLayout.text?.fontSize || undefined,
      textTransform: 'none',
      borderRadius: '0',
      fontWeight: 400 || button?.fontWeight || globalLayout.text?.fontWeight || 'bold',
      fontFamily: button?.fontFamily || globalLayout.text?.fontFamily || undefined,
      border: button?.border || undefined,
      textDecoration: 'underline'
   }
});


const OutfitEasyBox = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useEasyStyles();
   return <div className={classes.container}>
      <div>
         <StyledTypography variant={"h1"} className={classes.title}>
            {localizations.easyBoxTitle}
         </StyledTypography>
      </div>
      <div>
         <div className={classes.list}>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <TradeInIcon type={"main"}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection1Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection1Description}
                  </StyledTypography>
               </div>
            </div>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <PackItIcon type={"main"}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection2Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection2Description}
                  </StyledTypography>
               </div>
            </div>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <DropOffIcon type={"main"}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection3Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection3Description}
                  </StyledTypography>
               </div>
            </div>
         </div>
      </div>
   </div>
}


const useEasyStyles = makeStyles((theme: Theme) => ({
      container: {
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center'
      },
      title: {
         marginTop: '40px'
      },
      text: {
         marginBottom: '15px'
      },
      list: {
         display: 'flex',
         flexDirection: 'row',
         justifyContent: 'center',
         marginTop: '40px'
      },
      listItem: {
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         marginBottom: '20px',
         gap: '10px'
      },
      listAvatar: {},
      listText: {
         marginTop: '0',
         textAlign: 'center',
         maxWidth: '400px',
         padding: '0 20px 0 20px'
      },
      image: {
         width: '150px'
      },
      '@media only screen and (max-width: 800px)': {
         list: {
            flexDirection: 'column',
         },
         listItem: {
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '600px',
            gap: '10px'
         },
      }
   }),
);
